import { ExternalEventsTriStateProps } from '@/components/ExternalEventsTriState/ExternalEventsTriState.vue';
import { TimelineVariant } from '@/components/Timeline/allocation/types';
import type { FullHeightCreateButtonStatus } from '@/components/Timeline/components/FullHeightCreateButton.vue';
import { UpdateExternalEventEventLocationVisibilityDocument } from '@/generated/graphql';
import { useMutation } from '@vue/apollo-composable';
import { useFluent } from 'fluent-vue';
import { DateTime } from 'luxon';
import { computed, ref, Ref, toValue } from 'vue';
import { ExternalEventTimelineLocation, getStatusFromVisibility, getVisibilityFromStatus } from '../externalEvent';

type CreateAllocationPayload = {
    startDateTime: DateTime;
    endDateTime: DateTime;
};

type PerDayStatusMap = Map<string, Map<string, FullHeightCreateButtonStatus>>;

export function useExternalEventAllocations(
    variantsRef: Ref<TimelineVariant[]>,
    visibleVariants: Ref<string[]>,
    highlightedVariantId: Ref<string>,
    eventId: Ref<string>,
    locations: Ref<ExternalEventTimelineLocation[]>,
    setLocations: (value: ExternalEventTimelineLocation[]) => void,
    totalAllocationCounts: Ref<Map<string, number>>,
) {
    const { $t } = useFluent();

    // Per-location, per-day create button status: Location ID -> Day by ISO -> Status
    const perDayStatusMap = ref<PerDayStatusMap>(new Map());

    const updateLocationVisibility = useMutation(UpdateExternalEventEventLocationVisibilityDocument);

    const filteredVariants = computed(
        () => toValue(variantsRef).filter((v) => toValue(visibleVariants).includes(v.id)) ?? [],
    );

    const filteredAllocations = computed(() => {
        return filteredVariants.value.flatMap((variant) => variant.allocations.nodes);
    });

    const allocationsCount = computed(() => filteredAllocations.value.length);

    const referenceAllocationIds = computed(() => filteredVariants.value.flatMap((v) => v.referenceAllocationId ?? []));

    const referenceAllocations = computed(() => {
        return filteredAllocations.value
            .filter((a) => referenceAllocationIds.value.includes(a.id))
            .map((a) => ({
                ...a,
                id: `reference-${a.id}`,
                linkTarget: '',
                clickAction: { type: 'Jump-To-Allocation' as const },
            }));
    });

    const referenceAllocationFirstColumnCell = computed(() => {
        return {
            rowId: 'reference-allocation-row',
            allocationCount: {
                inRange: referenceAllocations.value.length,
                total: totalAllocationCounts.value.get('reference-allocation-row') ?? 0,
            },
            label: $t('reference-allocations'),
            tooltip: $t('reference-allocations-tooltip'),
        };
    });

    const firstColumnCells = computed(() =>
        locations.value.map((location) => {
            return {
                locationId: location.id,
                rowId: location.id,
                allocationCount: {
                    inRange: filteredAllocations.value.filter((a) => a.locationId === location.id).length,
                    total: totalAllocationCounts.value.get(location.id) ?? 0,
                },
                label: location.label,
                status: location.status,
            };
        }),
    );
    const layoutRows = computed(() =>
        locations.value.map((location) => {
            return {
                locationId: location.id,
                rowId: location.id,
                isFirstRow: false,
                layoutAlgorithm: 'Compact' as const,
                createButtonStatus:
                    toValue(highlightedVariantId) !== 'No-Highlight-Id'
                        ? {
                              type: 'Visible' as const,
                              rowLabel: location.label,
                              perDayStatus: perDayStatusMap.value.get(location.id) ?? new Map(),
                          }
                        : {
                              type: 'Disabled' as const,
                              rowLabel: location.label,
                              tooltipText: $t('external-event-no-variant-selected'),
                          },
                allocations: filteredAllocations.value
                    .filter((a) => a.locationId === location.id)
                    .sort((a, b) => a.startDateTime.toMillis() - b.startDateTime.toMillis()),
            };
        }),
    );

    const referenceAllocationsLayoutRow = computed(() => ({
        rowId: 'reference-allocation-row',
        allocationCount: referenceAllocations.value.length,
        isFirstRow: true,
        layoutAlgorithm: 'Allocation-Variant-Order' as const,
        createButtonStatus: { type: 'Hidden' as const },
    }));

    async function handleStatusChange(status: ExternalEventsTriStateProps['status'], locationId: string) {
        const location = locations.value.find((l) => l.id === locationId);
        if (location) {
            if (status !== location.status) {
                // set loading state
                const updatedLocations = locations.value.map((l) =>
                    l.id === locationId
                        ? { ...l, status: `${status}-Loading` as ExternalEventsTriStateProps['status'] }
                        : l,
                );
                setLocations(updatedLocations);

                const result = await updateLocationVisibility.mutate({
                    locationId: locationId,
                    eventId: toValue(eventId),
                    visibility: getVisibilityFromStatus(status),
                });
                if (result?.errors) {
                    // set error state
                    const updatedLocations = locations.value.map((l) =>
                        l.id === locationId
                            ? { ...l, status: `${status}-Error` as ExternalEventsTriStateProps['status'] }
                            : l,
                    );
                    setLocations(updatedLocations);
                    return;
                }
                if (result?.data && result?.data.update?.externalEventEventLocationVisibility?.visibility) {
                    const status = getStatusFromVisibility(
                        result?.data?.update?.externalEventEventLocationVisibility?.visibility,
                    );
                    const updatedLocations = locations.value.map((l) => {
                        if (l.id === locationId) {
                            return {
                                ...l,
                                status: status,
                            };
                        }
                        return l;
                    });
                    setLocations(updatedLocations);
                }
            }
        }
    }

    function setDayStatus(locationId: string, isoKey: string, status: FullHeightCreateButtonStatus) {
        const locationMap = new Map(perDayStatusMap.value); // Copy outer Map to trigger reactivity
        const dayMapCopy = new Map(locationMap.get(locationId) ?? new Map());
        dayMapCopy.set(isoKey, status);
        locationMap.set(locationId, dayMapCopy);
        perDayStatusMap.value = locationMap;
    }

    function clearDayStatus(locationId: string, isoKey: string) {
        const locationMap = new Map(perDayStatusMap.value); // Copy outer Map to trigger reactivity
        const dayMapCopy = new Map(locationMap.get(locationId) ?? new Map());
        dayMapCopy.delete(isoKey);
        if (dayMapCopy.size === 0) {
            locationMap.delete(locationId);
        } else {
            locationMap.set(locationId, dayMapCopy);
        }
        perDayStatusMap.value = locationMap;
    }

    async function onLayoutRowCreateAllocation(
        locationId: string,
        payload: CreateAllocationPayload,
        createAllocation: (
            locationId: string,
            startDateTime: DateTime,
            endDateTime: DateTime,
            onDone: (isSuccess: boolean) => void,
        ) => Promise<void>,
    ) {
        const isoKey = payload.startDateTime.toISO();
        if (!isoKey) {
            throw new Error('Conversion to ISO failed for start date time: ' + payload.startDateTime);
        }

        setDayStatus(locationId, isoKey, { type: 'Loading' });

        await createAllocation(locationId, payload.startDateTime, payload.endDateTime, (isSuccess: boolean) => {
            if (isSuccess) {
                clearDayStatus(locationId, isoKey);
            } else {
                setDayStatus(locationId, isoKey, { type: 'Retry' });
            }
        });
    }

    return {
        filteredAllocations,
        allocationsCount,
        referenceAllocationFirstColumnCell,
        referenceAllocationsLayoutRow,
        referenceAllocations,
        firstColumnCells,
        layoutRows,
        onLayoutRowCreateAllocation,
        handleStatusChange,
    };
}
